import { useLocation } from "react-router-dom";
import {
  LayoutDashboard, UsersRound, Users, MessageCircle, Mic,
  Brain, ShieldCheck, Settings, ChevronDown, LogOut,
  Activity, Radar, Zap, BarChart3, Workflow,
  BookOpen, Building2, Bot, Image as ImageIcon,
  BadgeDollarSign,
  Headphones, Sparkles, Crown, Flag,
  Bell, Handshake, Link2, Calendar, BookMarked,
  Stethoscope, Briefcase, Receipt, Timer, GaugeCircle,
  Vote, Database, FileText,
} from "lucide-react";
import { NavLink } from "@/components/NavLink";
import { SyraLogo } from "@/components/brand/SyraLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { UIModeToggle } from "@/components/UIModeToggle";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { useAuth } from "@/contexts/AuthContext";
import { useUIMode } from "@/hooks/useUIMode";
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarFooter, useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import type { UserRole } from "@/types/syra";
import { hasPermission, type Role } from "@/lib/rbac";

/**
 * Sprint 3 (P1-07/P1-08): Sidebar consolidada — máx 15 itens visíveis no modo Simples.
 *
 * Reorganização: 5 grupos canônicos do Doc 2 (Operação · Cortex · Memória · Modelos · Sistema)
 * + Administração (admin-only). Páginas técnicas/duplicadas movidas para modo "advanced"
 * ou acessíveis apenas via /admin → tabs.
 *
 * MT-UI-01 (Doc 5 v1.1): labels reescritos em JTBD ("Job to be done") — verbo de ação
 * no lugar de substantivo técnico. Ex: "Memórias" → "Ver memórias da SYRA".
 * Aplicado apenas aos grupos do usuário final; "Administração" mantém jargão SRE.
 *
 * Princípios:
 *  - Modo Simples: ≤15 entradas, todas com nomes traduzidos (sem jargão técnico).
 *  - Modo Avançado: revela ferramentas SRE / forense.
 *  - Jargão (TD-error, Locus Coeruleus, arousal, inhibitor) só aparece em /governanca/forense.
 */
type Item = {
  title: string;
  url: string;
  icon: typeof LayoutDashboard;
  mode?: "always" | "advanced";
  adminOnly?: boolean;
  /** Tooltip curto explicando o item — obrigatório para itens admin/advanced. */
  tooltip?: string;
};
type Section = {
  label: string;
  collapsible: boolean;
  items: Item[];
  adminOnly?: boolean;
};

const sections: Section[] = [
  {
    label: "Operação",
    collapsible: false,
    items: [
      { title: "Visão geral", url: "/dashboard", icon: LayoutDashboard, tooltip: "Página inicial: estado da SYRA, grupos ativos e atalhos do dia" },
      { title: "Gerenciar grupos", url: "/groups", icon: UsersRound, tooltip: "Lista de grupos do WhatsApp e configuração por grupo" },
      { title: "Cobrança por grupo", url: "/groups/billing", icon: Receipt, mode: "advanced", tooltip: "Relatório mensal de tokens e custo por grupo, exportável" },
      { title: "Acompanhar conversas", url: "/conversations", icon: MessageCircle, tooltip: "Histórico do que a SYRA respondeu nos grupos" },
      { title: "Falar com Jarvis (voz)", url: "/jarvis", icon: Mic, tooltip: "Conversa por voz com o assistente Jarvis" },
      { title: "Atender suporte", url: "/support", icon: Headphones, tooltip: "Tickets, base de conhecimento e SLA do suporte" },
      { title: "Apoiar vendas", url: "/sales", icon: Briefcase, mode: "advanced", tooltip: "Assistente comercial: pitches, capacidades e objeções" },
      { title: "Conhecer membros", url: "/members", icon: Users, mode: "advanced", tooltip: "CRM dos participantes dos grupos" },
      { title: "Gerar/ver mídia", url: "/media-ia", icon: ImageIcon, mode: "advanced", tooltip: "Histórico, saúde e pipeline multimodal em uma só tela" },
      { title: "Pedir ajuda ao copiloto", url: "/copilot", icon: Sparkles, mode: "advanced", tooltip: "Assistente operacional do admin" },
    ],
  },
  {
    label: "Inteligência",
    collapsible: true,
    items: [
      { title: "Ver o cérebro da SYRA", url: "/cortex-os", icon: Activity, tooltip: "Visão geral do cérebro da IA: pipeline, decisões e fluxo em tempo real" },
      { title: "Configurar personalidades", url: "/agents", icon: Bot, tooltip: "Editar tom, formalidade e proatividade dos agentes" },
      { title: "Ativar habilidades", url: "/skills", icon: Zap, tooltip: "Skills disponíveis (resumo, análise, busca etc.)" },
      { title: "Decidir qual modelo usar", url: "/model-routing", icon: Workflow, mode: "advanced", tooltip: "Políticas, decisões e provedores de modelo" },
    ],
  },
  {
    label: "Conhecimento",
    collapsible: true,
    items: [
      { title: "Ver memórias da SYRA", url: "/memory", icon: Brain, tooltip: "Tudo que a SYRA aprendeu e lembra entre conversas" },
      { title: "Editar base de conhecimento", url: "/knowledge-base", icon: BookOpen, tooltip: "Artigos, FAQ e respostas oficiais" },
      { title: "Conectar fontes & arquivos", url: "/sources", icon: FileText, tooltip: "Conexões externas e PDFs/TXT enviados que alimentam a SYRA" },
      { title: "Documentar no wiki", url: "/wiki", icon: BookMarked, mode: "advanced", tooltip: "Páginas internas e documentação operacional" },
    ],
  },
  {
    label: "Governança",
    collapsible: true,
    items: [
      { title: "Auditar com Sentinel", url: "/sentinel", icon: Radar, tooltip: "Auditoria automática de qualidade e drift da IA" },
      { title: "Revisar segurança", url: "/security", icon: ShieldCheck, tooltip: "Acessos, RLS e logs de segurança" },
    ],
  },
  {
    label: "Sistema",
    collapsible: true,
    items: [
      { title: "Ver uso & custo", url: "/usage", icon: Receipt, tooltip: "Consumo de IA por dia, semana e mês" },
      { title: "Gerenciar plano", url: "/billing", icon: BadgeDollarSign, tooltip: "Assinatura, limites e faturas" },
      { title: "Ajustar configurações", url: "/settings", icon: Settings, tooltip: "Preferências da organização" },
    ],
  },
  {
    label: "Atividade do grupo",
    collapsible: true,
    items: [
      { title: "Programar lembretes", url: "/reminders", icon: Bell, tooltip: "Lembretes que a SYRA dispara nos grupos" },
      { title: "Acompanhar compromissos", url: "/commitments", icon: Handshake, tooltip: "O que cada participante prometeu fazer" },
      { title: "Organizar links", url: "/link-library", icon: Link2, tooltip: "Biblioteca de links compartilhados nos grupos" },
      { title: "Criar enquetes", url: "/polls", icon: Vote, tooltip: "Enquetes lançadas pela SYRA nos grupos" },
    ],
  },
  {
    label: "Administração",
    collapsible: true,
    adminOnly: true,
    items: [
      { title: "Painel admin", url: "/admin", icon: Crown, adminOnly: true, tooltip: "Visão geral admin: usuários, billing e overrides" },
      { title: "Saúde + SLOs", url: "/admin/health", icon: Stethoscope, adminOnly: true, tooltip: "SLOs numéricos + gráficos por subsistema (WM, LTM, Skill, Multimodal, Factual, People Memory) e diagnóstico geral" },
      { title: "Cortex live", url: "/cortex-os/live", icon: Activity, adminOnly: true, mode: "advanced", tooltip: "Cards em tempo real do pipeline cognitivo" },
      { title: "Alerts & SLOs", url: "/admin/alerts-slos", icon: Bell, adminOnly: true, tooltip: "Regras de alerta e SLOs ativos" },
      { title: "Feature flags", url: "/sistema/flags", icon: Flag, adminOnly: true, tooltip: "Liga/desliga recursos e ajusta rollout" },
      { title: "Custo (sistema)", url: "/sistema/custo", icon: Receipt, adminOnly: true, mode: "advanced", tooltip: "Orçamentos e tetos por organização" },
      { title: "Latência (sistema)", url: "/sistema/latencia", icon: Timer, adminOnly: true, mode: "advanced", tooltip: "p50/p95/p99 por etapa do pipeline" },
      { title: "Prontidão comercial", url: "/sistema/prontidao-comercial", icon: GaugeCircle, adminOnly: true, mode: "advanced", tooltip: "Critérios GO/NO-GO para venda" },
      { title: "Governança forense", url: "/governanca/forense", icon: ShieldCheck, adminOnly: true, mode: "advanced", tooltip: "Telemetria interna avançada (jargão técnico)" },
      { title: "Scheduled skills", url: "/admin/scheduled-skills", icon: Calendar, adminOnly: true, tooltip: "Tarefas agendadas executadas pelas skills" },
      { title: "Memórias de Pessoas", url: "/admin/people-memory", icon: Users, adminOnly: true, tooltip: "Memória individual por participante" },
      { title: "Métricas de Memória", url: "/admin/memory-metrics", icon: Database, adminOnly: true, tooltip: "Hit rate, latência, tamanho consolidado e candidatos a backfill" },
      { title: "Glossário", url: "/admin/glossario", icon: BookOpen, adminOnly: true, tooltip: "Tradução de termos técnicos (ToM, Brier, shadow, drift...)" },
      { title: "Usuários", url: "/users", icon: Users, adminOnly: true },
      { title: "Organizações", url: "/organizations", icon: Building2, adminOnly: true },
    ],
  },
];

function SidebarSection({ section, collapsed, isAdmin, isAdvanced }: {
  section: Section; collapsed: boolean; isAdmin: boolean; isAdvanced: boolean;
}) {
  const location = useLocation();
  const items = section.items.filter((it) => {
    if (it.adminOnly && !isAdmin) return false;
    if (it.mode === "advanced" && !isAdvanced) return false;
    return true;
  });
  if (items.length === 0) return null;

  const hasActive = items.some((it) =>
    it.url === "/dashboard" ? location.pathname === "/dashboard" : location.pathname.startsWith(it.url),
  );

  const menu = (
    <SidebarMenu className="space-y-0.5">
      {items.map((item) => {
        const link = (
          <NavLink
            to={item.url}
            end={item.url === "/dashboard"}
            className="flex items-center min-w-0 rounded-lg transition-colors duration-200 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            activeClassName="bg-primary text-primary-foreground font-medium"
          >
            <item.icon className={`h-4 w-4 shrink-0 ${collapsed ? "" : "mr-2"}`} />
            {!collapsed && <span className="truncate whitespace-nowrap">{item.title}</span>}
          </NavLink>
        );
        const needsTooltip = collapsed || !!item.tooltip;
        return (
          <SidebarMenuItem key={item.url}>
            <SidebarMenuButton asChild>
              {needsTooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>{link}</TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    {item.tooltip ?? item.title}
                  </TooltipContent>
                </Tooltip>
              ) : (
                link
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );

  if (!section.collapsible || collapsed) {
    return (
      <SidebarGroup>
        {!collapsed && (
          <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4">
            {section.label}
          </SidebarGroupLabel>
        )}
        <SidebarGroupContent>{menu}</SidebarGroupContent>
      </SidebarGroup>
    );
  }

  return (
    <Collapsible defaultOpen={hasActive} className="group/collapsible">
      <SidebarGroup>
        <CollapsibleTrigger className="flex w-full items-center justify-between px-4 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors">
          {section.label}
          <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-data-[state=closed]/collapsible:rotate-[-90deg]" />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SidebarGroupContent>{menu}</SidebarGroupContent>
        </CollapsibleContent>
      </SidebarGroup>
    </Collapsible>
  );
}

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const { user, logout } = useAuth();
  const { isAdvanced } = useUIMode();

  const userRole = user?.role as UserRole | undefined;
  // Itens marcados `adminOnly` são gated por `admin.panel` no router (RoleGate).
  // Para evitar 404 ao clicar, usamos a MESMA permissão para esconder na sidebar.
  const isAdmin = hasPermission((userRole ?? null) as Role | null, "admin.panel");

  return (
    <Sidebar collapsible="icon">
      <SidebarContent className="bg-sidebar overflow-y-auto scrollbar-none">
        <div className={`py-4 flex items-center gap-2 ${collapsed ? "justify-center px-2" : "px-4"}`}>
          <SyraLogo size={26} variant="mark" />
          {!collapsed && (
            <span className="text-lg font-bold text-primary font-headline tracking-[0.18em]">SYRA</span>
          )}
        </div>

        <UIModeToggle collapsed={collapsed} />
        <OrgSwitcher collapsed={collapsed} />

        {sections
          .filter((s) => !s.adminOnly || isAdmin)
          .map((s) => (
            <SidebarSection
              key={s.label}
              section={s}
              collapsed={collapsed}
              isAdmin={isAdmin}
              isAdvanced={isAdvanced}
            />
          ))}
      </SidebarContent>
      <SidebarFooter className={`p-3 border-t border-sidebar-border ${collapsed ? "items-center" : ""}`}>
        {!collapsed && user && (
          <div className="text-xs text-muted-foreground mb-2 truncate">
            {user.name} ({user.role})
          </div>
        )}
        <div className={`flex items-center ${collapsed ? "flex-col gap-1" : "gap-1"}`}>
          <ThemeToggle showTooltip={collapsed} />
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground" onClick={logout}>
                  <LogOut className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Sair</TooltipContent>
            </Tooltip>
          ) : (
            <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground flex-1 justify-start" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
